import { useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useQuery } from '@apollo/client';
import { useAtomValue } from 'jotai';
import { FaultCodes } from '@feedflo/fault-codes';

import CollapsibleSection from '../../atoms/CollapsibleSection';
import Page from '../../atoms/Page';
import AnimalGroupHeader from '../../molecules/AnimalGroupHeader';
import FeedLineStatusCard from '../../organisms/FeedLineStatusCard';
import BinSetStatusCard from '../../organisms/BinSetStatusCard';
import BinControllerStatusCard from '../../organisms/BinControllerStatusCard';
import DeviceStatusCard from '../../organisms/DeviceStatusCard';
import DeviceConnectivityModal from '../../molecules/DeviceConnectivityModal';
import useFaultCodeFilter from '../../utils/useFaultCodeFilter';
import { computeInternetScore } from '../../utils/internetScore';
import { BinSetCalibrationStatusEnum, FeedLineFullStatus, FeedLineRunningStatus } from './enum';
import { processBinSetOrders } from '../../utils/orderUtils';
import { algorithmVersionAtom } from '../../utils/jotaiAtoms';

import useUser from '../../utils/hooks/useUser';
import { useFeedFrameFilter, useAnalysisFilter } from '../../utils/useFeedFrameFilter';

import { GetBarnSummaryDataQuery } from './queries';

import './BarnSummaryTab.scss';
import FeedLineStatusCardView from '../../organisms/FeedLineStatusCard/FeedLineStatusCardView';

const NUM_LOADING_FEED_LINES_AND_DEVICES = 4; // The number of feed line and device skeletons to display while loading

function BarnSummaryTab({
  loading: animalGroupHeaderLoading = false,
  titleSegments = [],
  className = '',
  barnId = '',
  organizationId = '',
}) {
  const BIN_PREDICTION_STALE_CUTOFF = useMemo(() => {
    return dayjs.tz().subtract(6, 'hour').unix();
  }, []);
  const device_data_start = useMemo(() => {
    return dayjs.tz().subtract(1, 'day').unix();
  }, []);
  const { user } = useUser();
  const isMetric = user?.isMetric || false;
  const algorithmVersion = useAtomValue(algorithmVersionAtom);

  // Modal state management
  const [isConnectivityModalOpen, setIsConnectivityModalOpen] = useState(false);
  const [selectedDeviceIndex, setSelectedDeviceIndex] = useState(0);
  const modalRef = useRef(null);

  const pageTitleSegments = ['Status', ...titleSegments];
  const [isBinSetSectionExpanded, setIsBinSetSectionExpanded] = useState(true);
  const [isAllSensorsSectionExpanded, setIsAllSensorsSectionExpanded] = useState(true);
  const feedFrameWhere = useFeedFrameFilter();
  const feedFrameAnalysisWhere = useAnalysisFilter();
  const feedLineCoefficientWhere = useAnalysisFilter();

  // Helper function to get nofeed_seconds regardless of override
  const getNofeedSeconds = (fault) => {
    // When override is set, the data comes from fault_nofeed_times array
    if (algorithmVersion && fault.fault_nofeed_times?.length > 0) {
      return fault.fault_nofeed_times[0].nofeed_time_in_seconds;
    }
    // When no override, use the computed field (may be null)
    return fault.nofeed_seconds;
  };

  const faultFilter = {
    _and: [
      { _or: useFaultCodeFilter() },
      { can_be_seen_live: { islivevisible: { _eq: true } } },
      { ended_at: { _is_null: true } },
    ],
  };

  const { loading: feedLineLoading, data: barnSummaryData } = useQuery(GetBarnSummaryDataQuery, {
    variables: {
      barnId,
      activeFaultWhere: faultFilter, // filters the active faults does not filter the barns
      deviceUnreachableFaultCode: FaultCodes.DEVICE_UNREACHABLE,
      emptyPipeFaultCode: FaultCodes.EMPTY_PIPE,
      augerRunningFaultCode: FaultCodes.AUGER_RUNNING,
      feedFrameWhere,
      feedFrameAnalysisWhere,
      binPredictionCutoff: BIN_PREDICTION_STALE_CUTOFF,
      feedLineCoefficientWhere,
      device_data_start,
      algorithmVersionOverride: algorithmVersion,
      hasAlgorithmOverride: !!algorithmVersion,
    },
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
    skip: !barnId || !feedFrameAnalysisWhere,
    pollInterval: 5 * 60 * 1000,
    onError: (error) => console.error(`Error querying page data: ${error}`),
  });

  const onToggleBinSetSection = () => {
    setIsBinSetSectionExpanded(!isBinSetSectionExpanded);
  };

  const onToggleAllSensorsSection = () => {
    setIsAllSensorsSectionExpanded(!isAllSensorsSectionExpanded);
  };

  const renderBinSetsSection = () => {
    if (feedLineLoading || false) {
      return Array(Math.ceil(NUM_LOADING_FEED_LINES_AND_DEVICES / 2))
        .fill()
        .map((_, i) => (
          <div className="BarnSummaryTab-binSetList-BinSetStatusRow" key={`BinSetStatusRow${i}`}>
            <BinSetStatusCard key={i} loading />
            <div className="BarnSummaryTab-binSetList-FeedLineHolder">
              {Array(2)
                .fill()
                .map((_, j) => {
                  return <FeedLineStatusCard key={`FeedLineStatusCard${j}`} loading={true} />;
                })}
            </div>
          </div>
        ));
    }

    const binInventoryEnabled =
      barnSummaryData?.farm[0]?.features_setting?.[0]?.settings?.bin_inventory_enabled === true;

    return (barnSummaryData?.farm?.[0]?.bin_sets ?? []).map((binSet) => {
      // Use the new utility function to process orders and bins
      const { bins, order } = processBinSetOrders(binSet);

      const predictionRow = binSet?.bin_set_level_predictions?.[0];
      const {
        bin_set_level_in_grams: binSetLevelInGrams,
        predicted_at: predictedAt,
        empty_at: emptyAt,
        earliest_delivery_at: earliestDeliveryAt,
        runout_prevention_order_deadline_at: runout_prevention_order_deadline_at,
        earliest_delivery_bin_set_level_in_grams,
        runout_prevention_bin_set_level_in_grams,
        created_at: lastUpdatedAt,
      } = predictionRow || {};
      const feedLineNames = binSet?.feed_lines?.map((feedLine) => feedLine?.name);
      const binController = binSet?.bin_controllers?.[0] || null;
      const activeLockdown = (() => {
        const faults = binController?.device?.active_lockdown_faults || [];
        if (faults.some((f) => f.code === FaultCodes.BIN_CONTROLLER_INTENTIONAL_LOCK_DOWN)) return 'intentional';
        if (faults.some((f) => f.code === FaultCodes.BIN_CONTROLLER_HARD_LOCK_DOWN)) return 'hard';
        if (faults.some((f) => f.code === FaultCodes.BIN_CONTROLLER_SOFT_LOCK_DOWN)) return 'soft';
        return null;
      })();

      return (
        <div className="BarnSummaryTab-binSetList-BinSetStatusRow" key={`BinSetStatusRow${binSet.id}`}>
          <BinSetStatusCard
            key={binSet.id}
            binSetId={binSet.id}
            bins={bins}
            isMetric={isMetric}
            binSetLevelInGrams={binSetLevelInGrams}
            predictedAt={predictedAt}
            emptyAt={emptyAt}
            earliest_delivery_bin_set_level_in_grams={earliest_delivery_bin_set_level_in_grams}
            runout_prevention_bin_set_level_in_grams={runout_prevention_bin_set_level_in_grams}
            earliestDeliveryAt={earliestDeliveryAt}
            runout_prevention_order_deadline_at={runout_prevention_order_deadline_at}
            feedLineNames={feedLineNames}
            order={order}
            lastUpdatedAt={lastUpdatedAt}
            hideInventoryLevels={!binInventoryEnabled}
            binController={binController}
            activeLockdown={activeLockdown}
            perBinLevelInGrams={predictionRow?.per_bin_level_in_grams}
          />
          <div className="BarnSummaryTab-binSetList-FeedLineHolder">
            {(binSet.feed_lines ?? []).map((feedLine) => {
              let nofeedTimeInSeconds = 0;
              let mostRecentFeedFrame = null;
              let mostRecentTransaction = null;
              let currentDevice = null;
              let devices = [];
              let calibrationStatus;
              let task;
              let activeFaultCodes = [];
              let isFeedLineFull = FeedLineFullStatus.Unknown;
              let isFeedLineRunning = FeedLineRunningStatus.Unknown;
              let isCalculating = false;

              feedLine?.device_assignments?.forEach((deviceAssignment) => {
                currentDevice = deviceAssignment?.device;
                mostRecentFeedFrame = currentDevice?.feed_frames?.[0];
                mostRecentTransaction = currentDevice?.most_recent_transaction?.[0];
                activeFaultCodes = activeFaultCodes.concat(currentDevice?.active_alertable_fault_codes);
                nofeedTimeInSeconds = currentDevice?.active_alertable_fault_codes
                  ?.map((fl) => getNofeedSeconds(fl))
                  ?.filter((seconds) => seconds != null)
                  .reduce((max, seconds) => Math.max(max, seconds), Number.MIN_SAFE_INTEGER);
                if (nofeedTimeInSeconds === Number.MIN_SAFE_INTEGER) {
                  nofeedTimeInSeconds = null;
                }
                isFeedLineFull = currentDevice?.active_alertable_fault_codes?.some(
                  (f) => f.code === FaultCodes.EMPTY_PIPE,
                )
                  ? FeedLineFullStatus.Empty
                  : FeedLineFullStatus.Full;

                isFeedLineRunning =
                  mostRecentFeedFrame?.duration === null
                    ? FeedLineRunningStatus.Running
                    : FeedLineRunningStatus.Stopped;

                const device = {
                  id: currentDevice?.id,
                  isOnline: currentDevice?.active_device_unreachable_fault?.length === 0,
                  serial: currentDevice?.serial,
                  feedLineName: feedLine?.name,
                  lastUploadInSeconds: mostRecentTransaction?.occured_at,
                };
                devices.push(device);

                calibrationStatus = BinSetCalibrationStatusEnum.Uncalibrated;

                isCalculating =
                  mostRecentFeedFrame?.duration !== null && !mostRecentFeedFrame?.feed_frame_analyses?.[0];

                if (feedLine?.feed_line_coefficients?.[0]) {
                  calibrationStatus = BinSetCalibrationStatusEnum.Calibrated;
                }

                task = feedLine?.bin_set.tasks?.[0]?.task;
                if (task) {
                  const due_at_date = dayjs(1000 * task.due_at);
                  const remind_at_date = dayjs(1000 * task.remind_at);
                  if (dayjs().isAfter(remind_at_date)) {
                    calibrationStatus = BinSetCalibrationStatusEnum.Due;
                  }
                  if (dayjs().isAfter(due_at_date)) {
                    calibrationStatus = BinSetCalibrationStatusEnum.PastDue;
                  }
                }
              });

              return (
                <FeedLineStatusCardView
                  key={feedLine.id}
                  feedLineId={feedLine.id}
                  nofeedTimeInSeconds={nofeedTimeInSeconds}
                  activeFaultCodes={activeFaultCodes}
                  feedLineName={feedLine.name}
                  feedLineLink={`/b/${barnId}/line/${feedLine.id}`}
                  isFeedLineFull={isFeedLineFull || ''}
                  isAugerRunning={isFeedLineRunning || ''}
                  isCalculating={isCalculating}
                  lastFeedingEventStartInSeconds={mostRecentFeedFrame?.started_at}
                  weightInGrams={mostRecentFeedFrame?.feed_frame_analyses?.[0]?.latest_estimated_mass_moved_in_grams}
                  durationInSeconds={mostRecentFeedFrame?.duration}
                  deviceList={devices}
                  calibrationStatus={calibrationStatus}
                  isMetric={isMetric}
                />
              );
            })}
          </div>
        </div>
      );
    });
  };

  // Create a comprehensive device list with feedline information for the modal
  const deviceListForModal = useMemo(() => {
    if (!barnSummaryData?.farm?.[0]?.bin_sets) return [];

    return barnSummaryData.farm[0].bin_sets.reduce((deviceList, binset) => {
      return deviceList.concat(
        binset?.feed_lines?.reduce((deviceList, feedLine) => {
          return deviceList.concat(
            feedLine?.device_assignments?.reduce((deviceList, deviceAssignment) => {
              // Add feedline information to each device
              const deviceWithFeedLine = {
                id: deviceAssignment?.device?.id,
                serial: deviceAssignment?.device?.serial,
                feedLineName: feedLine.name,
                feedLineId: feedLine.id,
              };
              deviceList.push(deviceWithFeedLine);
              return deviceList;
            }, []),
          );
        }, []),
      );
    }, []);
  }, [barnSummaryData]);

  const binControllerList = useMemo(() => {
    if (!barnSummaryData?.farm?.[0]?.bin_sets) return [];

    return barnSummaryData.farm[0].bin_sets.reduce((controllers, binSet) => {
      const deviceId = binSet?.bin_controllers?.[0]?.device_id;
      if (!deviceId) {
        return controllers;
      }

      controllers.push({
        deviceId,
        binNames: (binSet.bins ?? []).map((bin) => bin.name).filter(Boolean),
        isOnline: binSet.bin_controllers[0].device?.active_device_unreachable_fault?.length === 0,
      });
      return controllers;
    }, []);
  }, [barnSummaryData]);

  const renderDevicesSection = () => {
    if (feedLineLoading) {
      return (
        <div className="BarnSummaryTab-deviceList">
          {Array(NUM_LOADING_FEED_LINES_AND_DEVICES)
            .fill()
            .map((_, i) => (
              <DeviceStatusCard key={i} loading={feedLineLoading} />
            ))}
        </div>
      );
    }

    // Create a comprehensive device list with feedline information
    const deviceList = (barnSummaryData?.farm?.[0]?.bin_sets ?? []).reduce((deviceList, binset) => {
      return deviceList.concat(
        binset?.feed_lines?.reduce((deviceList, feedLine) => {
          return deviceList.concat(
            feedLine?.device_assignments?.reduce((deviceList, deviceAssignment) => {
              // Add feedline information to each device
              const deviceWithFeedLine = {
                ...deviceAssignment?.device,
                feedLineName: feedLine.name,
                feedLineId: feedLine.id,
              };
              deviceList.push(deviceWithFeedLine);
              return deviceList;
            }, []),
          );
        }, []),
      );
    }, []);

    if (!binControllerList.length && !deviceList?.length) {
      return <span>No Active Devices</span>;
    }

    const feedLineDeviceCards = deviceList.map((device) => {
      const mostRecentTransaction = device?.most_recent_transaction?.[0];
      let nofeed_seconds = device?.active_alertable_fault_codes
        ?.map((fl) => getNofeedSeconds(fl))
        ?.filter((seconds) => seconds != null)
        .reduce((max, seconds) => Math.max(max, seconds), Number.MIN_SAFE_INTEGER);
      if (nofeed_seconds === Number.MIN_SAFE_INTEGER) {
        nofeed_seconds = null;
      }

      const internetScore = computeInternetScore(device?.device_data_aggregate?.aggregate);
      const letterGrade = internetScore?.grade;
      const averageDelay = internetScore?.averageDelay ?? 0;
      const dataCoverage = internetScore?.dataCoverage ?? 0;

      return (
        <DeviceStatusCard
          key={device?.id}
          ffId={device?.id}
          isOnline={device?.active_device_unreachable_fault?.length === 0}
          serial={device?.serial}
          feedLineName={device.feedLineName}
          lastUploadInSeconds={mostRecentTransaction?.occured_at}
          internetScore={letterGrade}
          internetScoreDescription={
            <>
              {`Average Delay: ${averageDelay.toFixed(0)}s`}
              <br />
              {` Data Coverage: ${Math.min(100, Math.max(0, Math.min(1, dataCoverage || 0) * 100)).toFixed(0)}%`}
            </>
          }
          onViewConnectivity={(deviceId) => {
            // Find the device index in our device list
            const deviceIndex = deviceListForModal.findIndex((d) => d.id === deviceId);
            if (deviceIndex !== -1) {
              setSelectedDeviceIndex(deviceIndex);
              setIsConnectivityModalOpen(true);
            }
          }}
        />
      );
    });

    return (
      <div className="BarnSummaryTab-deviceList">
        {binControllerList.map((binController) => (
                <BinControllerStatusCard
                  key={binController.deviceId}
                  deviceId={binController.deviceId}
                  binNames={binController.binNames}
                  isOnline={binController.isOnline}
                />
        ))}
        {feedLineDeviceCards}
      </div>
    );
  };

  return (
    <Page className={`BarnSummaryTab ${className}`} titleSegments={pageTitleSegments}>
      <AnimalGroupHeader
        loading={animalGroupHeaderLoading}
        className="BarnSummaryTab-animalGroupHeader"
        barnId={barnId}
        orgId={organizationId}
        allowGroupSelection={false}
      />
      {
        <CollapsibleSection
          className="BarnSummaryTab-section"
          titleClassName="BarnSummaryTab-sectionTitle"
          title="Bins"
          isExpanded={isBinSetSectionExpanded}
          onExpandCollapse={onToggleBinSetSection}
        >
          <div className="BarnSummaryTab-binSetList">{renderBinSetsSection()} </div>
        </CollapsibleSection>
      }
      <CollapsibleSection
        className="BarnSummaryTab-section"
        titleClassName="BarnSummaryTab-sectionTitle"
        title="Devices"
        isExpanded={isAllSensorsSectionExpanded}
        onExpandCollapse={onToggleAllSensorsSection}
      >
        {renderDevicesSection()}
      </CollapsibleSection>
      <DeviceConnectivityModal
        ref={modalRef}
        isOpen={isConnectivityModalOpen}
        onClose={() => setIsConnectivityModalOpen(false)}
        title="Device Connectivity (Past 2 Weeks)"
        devices={deviceListForModal}
        initialDeviceIndex={selectedDeviceIndex}
      />
    </Page>
  );
}

BarnSummaryTab.propTypes = {
  loading: PropTypes.bool,
  titleSegments: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  barnId: PropTypes.string,
  organizationId: PropTypes.string,
};

export default BarnSummaryTab;
